import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiStepperItem, NUMBER_FROM_STEP_AOW, StepKeyAOW } from '../../model/client-onboarding/acquire-onboarding-wizard-stepper';
import { BehaviorSubject, catchError, distinctUntilChanged, finalize, map, Observable, of, shareReplay, startWith, tap } from 'rxjs';

export interface AcquireMerchant {
    id: number | string;
    name: string;
}

@Injectable({ providedIn: 'root' })
export class AcquireOnboardingWizardService {
    constructor(private http: HttpClient) { }

    private readonly _steps$ = new BehaviorSubject<ApiStepperItem[]>([]);
    readonly steps$ = this._steps$.asObservable().pipe(shareReplay(1));
    stepSaved$ = new BehaviorSubject<StepKeyAOW>('merchantInformation');
    get stepsSnapshot(): ApiStepperItem[] { return this._steps$.value; }
    private refreshInFlight = new Map<number, Observable<ApiStepperItem[]>>();
    private readonly acquirerOnboardingbaseUrl = 'acquirer-onboarding';
    private readonly acquireOnboardingWizardbaseUrl = 'acquire-onboarding-wizard';

    // stepper
    public getStepper(onboardingId: number): Observable<ApiStepperItem[]> {
        const url = `${this.acquirerOnboardingbaseUrl}/${onboardingId}/steppers`;
        return this.http.get<{ result: ApiStepperItem[] }>(url).pipe(
            map(res => res.result ?? [])
        );
    }

    isBlocked$(currentStep: number, onboardingId: number) {
        return this.refresh(onboardingId).pipe(
            startWith(this.stepsSnapshot),
            map((items) => {
                const stepNum =
                    typeof currentStep === 'number'
                        ? currentStep
                        : /^\d+$/.test(currentStep as string)
                            ? Number(currentStep)
                            : NUMBER_FROM_STEP_AOW[currentStep as keyof typeof NUMBER_FROM_STEP_AOW];
                if (!items || items.length === 0) return null as unknown as boolean;
                const it = items.find(x => +x.step === stepNum);
                const raw = (it?.status ?? '').toString().trim().toLowerCase().replace(/[\s_-]/g, '');
                const open = raw === 'inprogress' || raw === 'completed';
                return !open;
            }),
            distinctUntilChanged(),
            catchError(() => of(false)),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    refresh(onboardingId: number): Observable<ApiStepperItem[]> {
        const inFlight = this.refreshInFlight.get(onboardingId);
        if (inFlight) return inFlight;

        const url = `${this.acquirerOnboardingbaseUrl}/${onboardingId}/steppers`;
        const request$ = this.http.get<{ result: ApiStepperItem[] }>(url).pipe(
            map(res => res?.result ?? []),
            tap(items => {
                this._steps$.next(items);
            }),
            catchError(err => {
                console.error('[refresh] error loading steps', err);
                return of(this.stepsSnapshot);
            }),
            finalize(() => this.refreshInFlight.delete(onboardingId)),
            shareReplay(1),
        );
        this.refreshInFlight.set(onboardingId, request$);
        return request$;
    }

    public getServicesWithPrices() {
        return this.http.get(`${this.acquirerOnboardingbaseUrl}/services-with-prices`);
    }

    fetchIfServicesOnboarded(acquirerOnboardingId: number, merchantOnboardingId: number){
        return this.http.get(`${this.acquirerOnboardingbaseUrl}/${acquirerOnboardingId}/services-onboarded/${merchantOnboardingId}`);
    }

    fetchAcquirerOnboarding(id: number): Observable<{ result: { id: number; fdClientId: number } }> {
        return this.http.get<{ result: { id: number; fdClientId: number } }>(`${this.acquirerOnboardingbaseUrl}/${id}`);
    }

    createDemoAcquirerOnboarding() {
        return this.http.post<{ result: any }>(`${this.acquirerOnboardingbaseUrl}/demo`, {});
    }

    saveStepData(payload: any) {
        return this.http.post(`${this.acquirerOnboardingbaseUrl}/save-client-data`, payload);
    }

    getMerchants(acquirerOnboardingId: number): Observable<AcquireMerchant[]> {
        return this.http.get<{ result: AcquireMerchant[] }>(`${this.acquirerOnboardingbaseUrl}/${acquirerOnboardingId}/merchants`)
            .pipe(map(res => res.result ?? []));
    }

    getMerchantInformation(merchantId: number) {
        return this.http.get<{ result: any }>(`${this.acquirerOnboardingbaseUrl}/${merchantId}/merchant-information`)
            .pipe(map(res => res.result));
    }

    getMerchantInformationByMerchantId(merchantId: number, acquirerOnboardingId: number) {
        return this.http.get<{ result: any }>(`${this.acquirerOnboardingbaseUrl}/${acquirerOnboardingId}/merchant-onboarding/${merchantId}/merchant-information`)
            .pipe(map(res => res.result));
    }

    // mids
    getStepMidData(merchantOnboardingId: number) {
        const url = `onboarding-wizard/mids/${merchantOnboardingId}`;
        return this.http.get<any>(url).pipe(map(res => res.result));
    }

    deleteMidData(midId: number) {
        return this.http.delete(`${this.acquireOnboardingWizardbaseUrl}/mids/${midId}`);
    }

    // services
    getHiredServices(acquirerOnboardingId: number) {
        const url = `${this.acquirerOnboardingbaseUrl}/services/${acquirerOnboardingId}`;
        return this.http.get<any>(url)
            .pipe(map(res => res.result));
    }

    //save mids draft 
    saveDraft(payload: any) {
        return this.http.post(`${this.acquireOnboardingWizardbaseUrl}/autosave`, payload)
    }

    finishOnboarding(id: number | string) {
        return this.http.patch<{result: any}>(`${this.acquirerOnboardingbaseUrl}/${id}/finish`, {});
    }

    checkRelationshipWithMerchant(acquirerOnboardingId: number,merchantOnboardingId: number) {
        return this.http.get<{ result: boolean }>(`${this.acquirerOnboardingbaseUrl}/relationship/${acquirerOnboardingId}/${merchantOnboardingId}`);
    }

    startOnboarding(){
        return this.http.post<{ result: any }>(`${this.acquirerOnboardingbaseUrl}/start-onboarding`, {});
    }
}
